// Helper de streaming SSE pro chat RAG (P06 / DT-07).
//
// Usa `fetch` direto em vez de axios porque o axios não expõe o `ReadableStream`
// do response body (necessário pra ler token-a-token). `EventSource` também
// não serve — só faz GET e não aceita header `Authorization`.

import config from '@/config';
import {
  QuotaErrorResponse,
  StreamDonePayload,
  StreamErrorPayload,
  StreamHandlers,
  StreamPhase,
} from '@/types/agents';
import { handleQuotaError } from '@/utils/quotaErrorHandler';

const REFRESH_PATH = '/auth/refresh/';

/** Erro de cota (HTTP 402) preservando o payload estruturado do backend. */
export class QuotaStreamError extends Error {
  data: QuotaErrorResponse;

  constructor(data: QuotaErrorResponse) {
    super(data.message || 'Cota excedida.');
    this.name = 'QuotaStreamError';
    this.data = data;
  }
}

export function isQuotaStreamError(err: unknown): err is QuotaStreamError {
  return err instanceof QuotaStreamError || (err as { name?: string })?.name === 'QuotaStreamError';
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    const resp = await fetch(`${config.api.baseURL}${REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { access_token?: string };
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      return data.access_token;
    }
  } catch {
    /* swallow — caller trata como sessão expirada */
  }
  return null;
}

async function postStream(url: string, body: string, signal?: AbortSignal): Promise<Response> {
  const token = localStorage.getItem('access_token');
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  });
}

interface ParsedBlock {
  event: string;
  data: unknown;
}

/** Parseia um bloco SSE bruto (linhas `event:`/`data:`). Retorna null se inválido. */
function parseBlock(raw: string): ParsedBlock | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const sep = line.indexOf(':');
    if (sep < 0) continue;
    const field = line.slice(0, sep);
    const value = line.slice(sep + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

/**
 * Envia uma mensagem pra um agente RAG via SSE.
 *
 * O backend (P06) persiste a `user_message` antes do primeiro byte e emite os
 * eventos `status` (embed/search/llm_start) → `token`* → `done` (ou `error`).
 * Esse helper transforma os bytes em chamadas tipadas pros `handlers`.
 *
 * Lança `QuotaStreamError` em 402 (com o payload do backend) e `Error` genérico
 * em outros 4xx/5xx ou stream cortado. Em 401, tenta 1 refresh manual antes de
 * propagar. Aborto via `signal` é silencioso (chama o caller via `AbortError`).
 */
export async function streamSendMessage(
  conversationId: string,
  content: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${config.api.baseURL}/agents/conversations/${conversationId}/messages/`;
  const body = JSON.stringify({ content });

  let response = await postStream(url, body, signal);

  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      throw new Error('Sessão expirada. Faça login de novo.');
    }
    response = await postStream(url, body, signal);
  }

  if (response.status === 402) {
    const data = (await response.json()) as QuotaErrorResponse;
    // P14: dispara o modal global (interceptor do axios não roda no fetch
    // direto que o stream usa). Caller ainda recebe a exceção tipada pra
    // parar fluxo e limpar UI.
    if (data?.error === 'quota_exceeded') {
      handleQuotaError(data);
    }
    throw new QuotaStreamError(data);
  }

  if (!response.ok || !response.body) {
    throw new Error(`stream_failed:${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });

      let idx = buffer.indexOf('\n\n');
      while (idx >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseBlock(raw);
        if (parsed) {
          switch (parsed.event) {
            case 'status':
              handlers.onStatus?.((parsed.data as { phase: StreamPhase }).phase);
              break;
            case 'token':
              handlers.onToken?.((parsed.data as { content: string }).content);
              break;
            case 'done':
              handlers.onDone?.(parsed.data as StreamDonePayload);
              break;
            case 'error':
              handlers.onError?.(parsed.data as StreamErrorPayload);
              break;
            default:
            // ignora eventos desconhecidos (forward-compat)
          }
        }
        idx = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* swallow */
    }
  }
}
